import { useMemo } from 'react';
import { useLoaderData, useParams, useSearchParams } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { MemberRepository, Users } from 'src/api/member_repository';
import { DEFAULT_PAGE_SIZE } from 'src/components/pagination/use_pagination';
import { useEffectiveSearchParams } from 'src/hooks/use_effective_search_params';
import { UserFieldsFragment } from 'src/types';

interface UseMembersReturn {
  members: UserFieldsFragment[];
  totalCount: number;
  page: number;
  perPage: number;
  handlePageChange: (newPage: number) => void;
}

export function useMembers(
  memberRepository: MemberRepository,
  useLoader: boolean = true,
): UseMembersReturn {
  const { organization } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const effectiveSearchParams = useEffectiveSearchParams();

  const page = parseInt(effectiveSearchParams.get('page') || '1');
  const perPage = DEFAULT_PAGE_SIZE;

  const loaderData = useLoaderData() as { queryKey: string[] } | null;

  // Create stable query key
  const queryKey = useMemo(() => {
    if (useLoader && loaderData?.queryKey) {
      return loaderData.queryKey;
    }

    return ['members', { page, perPage, organizationName: organization }];
  }, [useLoader, loaderData?.queryKey, page, perPage, organization]);

  const { data } = useSuspenseQuery<Users>({
    queryKey,
    queryFn: async () => {
      return memberRepository.getMembers({
        page,
        perPage,
        organizationName: organization!,
      });
    },
    // Prevent refetching when in background mode
    refetchOnMount: useLoader,
    refetchOnWindowFocus: useLoader,
  });

  const members: UserFieldsFragment[] = data.items;
  const totalCount = data?.totalCount || 0;

  const handlePageChange = (newPage: number) => {
    searchParams.set('page', newPage.toString());
    setSearchParams(searchParams);
  };

  return {
    members,
    totalCount,
    page,
    perPage,
    handlePageChange,
  };
}
